import type { RetargetRegionPort, RetargetViewSnapshot } from './inspection-retarget-region-port.service';

/**
 * Region-switch lifecycle for the inspection retarget tool (Angular-free).
 *
 * Owns the origin view snapshot (taken before the tool's first region switch), the region the tool
 * last showed, the chosen target region and the wait for that region's properties to load.
 * Both Cancel/close and a successful Apply end with `returnToOrigin()`; `forget()` is for when the
 * tool is ended by someone else (functionality mode change, foreign region highlight) and never navigates.
 */
export class RetargetRegionSwitch {
  private origin: RetargetViewSnapshot | null = null;
  private shownByTool: string | null = null;
  private generation = 0;
  public switching = false;
  public chosenRegionId: string | null = null;

  constructor(private port: () => RetargetRegionPort | null, private waitMs = 30000, private pollMs = 150) {}

  /**
   * Shows `regionId` (no navigation if already shown) and resolves true once its properties are loaded.
   * Resolves false when there is no port, a switch is already pending, the wait times out, or `forget()` aborts it.
   */
  async choose(regionId: string): Promise<boolean> {
    const port = this.port();
    if (!port || this.switching) return false;
    this.chosenRegionId = regionId;
    if (port.shownRegionId() !== regionId) {
      if (!this.origin) this.origin = port.snapshotView();
      port.showRegion(regionId);
      this.shownByTool = regionId;
    }
    const gen = ++this.generation;
    this.switching = true;
    try {
      return await this.waitReady(port, regionId, gen);
    } finally {
      if (gen === this.generation) this.switching = false;
    }
  }

  /**
   * Cancel/close and successful Apply: restores the origin view if the region the tool last showed is
   * still shown (no-op after foreign navigation or when the tool never switched), then forgets everything.
   */
  returnToOrigin(): boolean {
    const port = this.port();
    const origin = this.origin;
    const shownByTool = this.shownByTool;
    this.forget();
    if (!port || !origin || shownByTool === null || port.shownRegionId() !== shownByTool) return false;
    port.restoreView(origin);
    return true;
  }

  /** True while the view shows the region this tool navigated to (not one the user navigated to). */
  isShowingToolRegion(): boolean {
    return this.shownByTool !== null && this.port()?.shownRegionId() === this.shownByTool;
  }

  /** Tool ended by someone else: drop all state and abort any pending wait; never navigates. */
  forget(): void {
    this.origin = null;
    this.shownByTool = null;
    this.chosenRegionId = null;
    this.generation++;
    this.switching = false;
  }

  private async waitReady(port: RetargetRegionPort, regionId: string, gen: number): Promise<boolean> {
    const deadline = Date.now() + this.waitMs;
    for (;;) {
      if (gen !== this.generation) return false;
      if (port.isRegionReady(regionId)) return true;
      if (Date.now() >= deadline) return false;
      await new Promise<void>(resolve => setTimeout(resolve, this.pollMs));
    }
  }
}
